import { Area, Size } from "react-easy-crop/types"

const jpegQuality = 0.8

export function cropImage(
  image: HTMLImageElement,
  canvas: HTMLCanvasElement,
  cropRect: Area,
  mirror = false,
): string {
  resizeCanvas(canvas, image.naturalWidth)

  var context = canvas.getContext("2d")
  if (!context) {
    throw Error("Unable to retrive context from canvas")
  }

  let xInverter = 1
  if (mirror) {
    context.scale(-1, 1)
    xInverter = -1
  }

  context.drawImage(
    image,
    cropRect.x,
    cropRect.y,
    cropRect.width,
    cropRect.height,
    0,
    0,
    canvas.width * xInverter,
    canvas.height,
  )

  return canvas.toDataURL("image/jpeg", 0.8)
}

export function rotateImage(image: HTMLImageElement, rotation = 0): string {
  const canvas = document.createElement("canvas")
  const context = canvas.getContext("2d")
  if (!context) {
    throw Error("Unable to retrive context from canvas")
  }

  const orientationChanged =
    rotation === 90 || rotation === -90 || rotation === 270 || rotation === -270
  if (orientationChanged) {
    canvas.width = image.height
    canvas.height = image.width
  } else {
    canvas.width = image.width
    canvas.height = image.height
  }

  context.translate(canvas.width / 2, canvas.height / 2)
  context.rotate((rotation * Math.PI) / 180)
  context.drawImage(image, -image.width / 2, -image.height / 2)

  return canvas.toDataURL("image/jpeg", jpegQuality)
}

export function compose(
  userImage: HTMLImageElement,
  overlayImage: HTMLImageElement,
  backgroundImage: HTMLImageElement,
  stickers: HTMLImageElement[],
  canvas: HTMLCanvasElement,
) {
  resizeCanvas(canvas, userImage.naturalWidth)
  layer(null, userImage, backgroundImage, canvas)

  for (let sticker of stickers) {
    drawSticker(sticker, canvas)
  }

  layer(overlayImage, null, null, canvas)

  return canvas.toDataURL("image/jpeg", jpegQuality)
}

function resizeCanvas(canvas: HTMLCanvasElement, originalWidth: number) {
  const maxWidth = 2560
  const sideLength = Math.min(originalWidth, maxWidth)
  canvas.width = sideLength
  canvas.height = sideLength
}

function layer(
  topImage: HTMLImageElement | null,
  middleImage: HTMLImageElement | null,
  bottomImage: HTMLImageElement | null,
  canvas: HTMLCanvasElement,
) {
  var context = canvas.getContext("2d")
  if (!context) {
    throw Error("Unable to retrive context from canvas")
  }

  if (bottomImage) {
    context.drawImage(bottomImage, 0, 0, canvas.width, canvas.height)
  }

  if (middleImage) {
    context.drawImage(middleImage, 0, 0, canvas.width, canvas.height)
  }

  if (topImage) {
    context.drawImage(topImage, 0, 0, canvas.width, canvas.height)
  }
}

export function drawSticker(sticker: HTMLImageElement, canvas: HTMLCanvasElement) {
  interface StickerDataset extends DOMStringMap {
    radians: string
    scale: string
  }

  let dataset = sticker.dataset as StickerDataset
  let radians = parseFloat(dataset.radians)
  let scale = parseFloat(dataset.scale)

  let aspectRatio = sticker.clientWidth / sticker.clientHeight
  let width = (parseFloat(sticker.style.width) / 100) * canvas.width
  let height = width / aspectRatio

  let rect = {
    x: (parseFloat(sticker.style.left) / 100) * canvas.width,
    y: (parseFloat(sticker.style.top) / 100) * canvas.height,
    width: width,
    height: height,
  }

  let img = sticker.querySelector(":scope > img") as HTMLImageElement
  var context = canvas.getContext("2d")
  if (!context) {
    throw Error("Unable to retrive context from canvas")
  }

  context.save()
  context.translate(rect.x + rect.width / 2, rect.y + rect.height / 2)
  context.scale(scale, scale)
  context.rotate(radians)
  context.drawImage(img, -rect.width / 2, -rect.height / 2, rect.width, rect.height)
  context.restore()
}

export function processImageFile(imageFile: Blob, callback: (result: string) => void) {
  const reader = new FileReader()
  reader.readAsDataURL(imageFile)
  reader.onload = async () => {
    callback(reader.result as string)
  }
}

export function checkImageSize(
  imageData: string,
  minimumSize: Size,
  completion: (acceptable: boolean) => void,
) {
  let image = new Image()
  image.src = imageData
  image.onload = function () {
    completion(image.naturalWidth >= minimumSize.width && image.naturalHeight >= minimumSize.height)
  }
}
